/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Modal } from 'react-bootstrap';
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from 'components/base/Button';
import orderItemService from 'service/orderItem';
import { showAlert } from 'store/alert/alert.thunk';
import NewAppSearchTable from './NewSearchTable';
import './styles.scss';

export type SearchData = {
  product_name: string;
  variation: string;
  barcode: string;
  units: string;
};

interface NewApplicationsTableSearchProps {
  senderPid?: any;
  orderItemData?: any;
  setOrderItemData?: any;
  focusRef?: any;
  isAutoTab?: any;
  chartererData?: any;
}

const NewApplicationsTableSearch = ({
  senderPid,
  orderItemData = [],
  setOrderItemData,
  focusRef,
  isAutoTab,
  chartererData
}: NewApplicationsTableSearchProps) => {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const { currency_pid } = state;

  const dispatch: Dispatch<any> = useDispatch();

  const [openSearch, setOpenSearch] = useState(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [isHandleSave, setIshandleSave] = useState(false);
  const [selectedData, setSelectedData] = useState<any>([]);

  const handleGetProduct = useCallback(
    async (e: any) => {
      if (e.key !== 'Enter') return;

      if (senderPid && searchedValue && currency_pid) {
        try {
          const res = await orderItemService
            .getByLegalEntityId(senderPid, {
              offset: 0,
              limit: 20,
              currency_pid: currency_pid,
              barcode: searchedValue ? searchedValue : undefined
            })
            .then((res: any) => res);

          const resData = res?.products ?? [];

          const createdData = resData.map((items: any) => {
            const barcode =
              res?.is_product === true
                ? items?.barcode
                : items?.nomenclature?.barcode;

            const gupid =
              res?.is_product === true ? items?.pid : items?.nomenclature?.pid;
            return {
              product_name: items?.nomenclature?.name,
              variation: items?.nomenclature?.is_variation ? t('yes') : t('no'),
              barcode: barcode,
              units: items?.nomenclature?.measure_unit?.name?.[i18n?.language],

              // This data is needed for static rendering
              unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
              measure_unit_pid: items?.nomenclature?.measure_unit?.pid || 0,
              amount: items?.quantity ?? 0,
              full_cost: items?.price_whosale ?? 0,
              nomenclature_pid: items?.nomenclature_pid || 0,
              product_pid: items?.pid || 0,
              product_parent_pid: items?.pid || 0,
              price: items?.price ?? 0,
              price_in: items?.price_in ?? 0,
              price_whosale: items?.price_whosale ?? 0,
              package_quantity: items?.package_quantity,
              currency_pid: items?.currency_pid || 0,
              is_vat: items?.is_vat ? items?.is_vat : false,
              measure_unit_kind_pid:
                items?.nomenclature?.measure_unit_kind_pid || 0,
              currency: items?.currency?.name?.[i18n?.language],
              remainder: items?.quantity ?? 0,
              gupid: gupid || 0
            };
          });

          if (createdData.length === 0) {
            dispatch(
              showAlert({
                title: t('barcode_not_found')
              })
            );
            return;
          }

          if (orderItemData?.length > 0) {
            const foundProductIndex = orderItemData.findIndex(
              (item: any) => item?.barcode === searchedValue
            );

            if (foundProductIndex !== -1) {
              const updatedProductInvoiceData = [...orderItemData];
              updatedProductInvoiceData[foundProductIndex].amount += 1;
              setOrderItemData(updatedProductInvoiceData);
            } else {
              setOrderItemData((prevData: any) => {
                const newData = [...createdData, ...prevData];

                if (isAutoTab) {
                  // when auto tab true focus to quantity
                  setTimeout(() => {
                    const firstProductIndex = 0;
                    focusRef(`amount_${firstProductIndex}`);
                  }, 500);
                }
                return newData;
              });
            }
          } else {
            setOrderItemData((prevData: any) => {
              const newData = [...createdData, ...prevData];

              if (isAutoTab) {
                // when auto tab true focus to quantity
                setTimeout(() => {
                  const firstProductIndex = 0;
                  focusRef(`amount_${firstProductIndex}`);
                }, 500);
              }
              return newData;
            });
          }
        } catch (error) {
          dispatch(
            showAlert({
              title: t('barcode_not_found')
            })
          );
        }
      }
    },
    [senderPid, searchedValue, orderItemData, dispatch, isAutoTab, focusRef]
  );

  return (
    <div className="mt-4">
      <div className="app-rows">
        <Form.Floating className="md-4">
          <Form.Control
            value={searchedValue}
            type="text"
            placeholder={t('scan_barcode')}
            onChange={e => setSearchedValue(e?.target?.value)}
            onKeyDown={e => handleGetProduct(e)}
            onFocus={e => e.target.select()}
          />
          <label htmlFor="floatingInputCustom">{t('scan_barcode')}</label>
        </Form.Floating>

        <Form.Group className="app-btn">
          <Button
            type="button"
            variant="primary"
            onClick={() => setOpenSearch(true)}
          >
            {t('search')}
          </Button>
        </Form.Group>
      </div>

      <Modal
        show={openSearch}
        onHide={() => {
          if (selectedData?.length > 0) {
            setIshandleSave(true);
          } else {
            setOpenSearch(false);
          }
        }}
        size="xl"
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>{t('search')}</Modal.Title>
          <Button
            style={{
              color: '#000',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '8px'
            }}
            disabled={isHandleSave}
            onClick={() => {
              if (selectedData?.length > 0) {
                setIshandleSave(true);
              } else {
                setOpenSearch(false);
              }
            }}
          >
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <NewAppSearchTable
            chartererData={chartererData}
            setOpenSearch={setOpenSearch}
            isHandleSave={isHandleSave}
            setIshandleSave={setIshandleSave}
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            setOrderItemData={setOrderItemData}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NewApplicationsTableSearch;
